import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable, of} from "rxjs";
import {catchError, map, tap} from "rxjs/operators";
import {firstValueFrom} from "rxjs";
import {environment} from "../../environments/environment";
import {AiModel} from "./ai-model";
import {SessionService} from "../state/session.service";

@Injectable({
  providedIn: 'root'
})
export class ModelCatalogService {
  private readonly modelsSubject = new BehaviorSubject<AiModel[]>([]);
  private readonly idLookup = new Map<string, string>();
  private loadPromise: Promise<void> | null = null;

  constructor(private readonly httpClient: HttpClient,
              private readonly sessionService: SessionService) {
    if (this.sessionService.aiModels?.length) {
      this.updateModels(this.sessionService.aiModels);
    }
  }

  loadModels(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = firstValueFrom(
      this.httpClient.get<AiModel[]>(environment.API_URL + '/api/ai-model').pipe(
        tap((models) => this.updateModels(models ?? [])),
        catchError((error) => {
          console.error('Failed to load AI models', error);
          return of(this.modelsSubject.value);
        }),
        map(() => void 0)
      )
    ).catch(() => void 0);

    return this.loadPromise;
  }

  async refresh(): Promise<void> {
    this.loadPromise = null;
    await this.loadModels();
  }

  getModels(): Observable<AiModel[]> {
    return this.modelsSubject.asObservable();
  }

  private updateModels(models: AiModel[]): void {
    const sortedModels = [...models].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    this.modelsSubject.next(sortedModels);
    this.sessionService.setAiModels(sortedModels);
  }
}
